/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { memo, createContext, useContext } from 'react';
import { AlertCircleIcon } from 'lucide-react';
import { CheckIcon } from 'lucide-react';
import { ChevronDownIcon } from 'lucide-react';
import { LoaderIcon } from 'lucide-react';
import { XCircleIcon } from 'lucide-react';
import { ToolCallMessagePartStatus } from '@assistant-ui/react';
import { ToolCallMessagePartComponent } from '@assistant-ui/react';
import cn from 'classnames';

/* ------------------------------------------------------------------ */
/* Context                                                            */
/* ------------------------------------------------------------------ */

type ToolFallbackContextValue = {
  open: boolean;
  setOpen: (v: boolean) => void;
};

const ToolFallbackContext = createContext<ToolFallbackContextValue | null>(
  null,
);

const useToolFallback = () => {
  const ctx = useContext(ToolFallbackContext);
  if (!ctx) {
    throw new Error('ToolFallback components must be inside Root');
  }
  return ctx;
};

/* ------------------------------------------------------------------ */
/* Root                                                               */
/* ------------------------------------------------------------------ */

export type ToolFallbackRootProps = {
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
};

const ToolFallbackRoot: React.FC<ToolFallbackRootProps> = ({
  children,
  defaultOpen = false,
  className,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <ToolFallbackContext.Provider value={{ open, setOpen }}>
      <div className={cn('w-full rounded-lg border py-3', className)}>
        {children}
      </div>
    </ToolFallbackContext.Provider>
  );
};

/* ------------------------------------------------------------------ */
/* Trigger                                                            */
/* ------------------------------------------------------------------ */

type ToolStatus = ToolCallMessagePartStatus['type'];

const statusIconMap: Record<ToolStatus, React.ElementType> = {
  running: LoaderIcon,
  complete: CheckIcon,
  incomplete: XCircleIcon,
  'requires-action': AlertCircleIcon,
};

const ToolFallbackTrigger: React.FC<{
  toolName: string;
  status?: ToolCallMessagePartStatus;
}> = ({ toolName, status }) => {
  const { open, setOpen } = useToolFallback();

  const statusType = status?.type ?? 'complete';
  const Icon = statusIconMap[statusType];

  return (
    <button
      onClick={() => setOpen(!open)}
      className="flex w-full items-center gap-2 px-4 text-sm"
    >
      <Icon className="size-4" />
      <span className="grow text-left">
        Used tool: <b>{toolName}</b>
      </span>
      <ChevronDownIcon
        className={cn(
          'size-4 transition-transform',
          open ? 'rotate-0' : '-rotate-90',
        )}
      />
    </button>
  );
};

/* ------------------------------------------------------------------ */
/* Content                                                            */
/* ------------------------------------------------------------------ */

const ToolFallbackContent: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { open } = useToolFallback();

  if (!open) return null;

  return <div className="mt-3 border-t pt-2 px-4 text-sm">{children}</div>;
};

/* ------------------------------------------------------------------ */
/* Args                                                               */
/* ------------------------------------------------------------------ */

function ToolFallbackArgs({
  argsText,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  argsText?: string;
}) {
  if (!argsText) return null;

  return (
    <div className={cn('px-4', className)} {...props}>
      <pre className="whitespace-pre-wrap">{argsText}</pre>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Result                                                             */
/* ------------------------------------------------------------------ */

function ToolFallbackResult({
  result,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  result?: unknown;
}) {
  if (result === undefined) return null;

  return (
    <div
      className={cn('border-t border-dashed px-4 pt-2', className)}
      {...props}
    >
      <p className="font-semibold">Result:</p>
      <pre className="whitespace-pre-wrap">
        {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
      </pre>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Error                                                              */
/* ------------------------------------------------------------------ */

function ToolFallbackError({
  status,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  status?: ToolCallMessagePartStatus;
}) {
  if (status?.type !== 'incomplete') return null;

  const error = status.error;
  const errorText =
    typeof error === 'string' ? error : error ? JSON.stringify(error) : null;

  if (!errorText) return null;

  const isCancelled = status.reason === 'cancelled';

  return (
    <div className={cn('px-4', className)} {...props}>
      <p className="font-semibold text-gray-500">
        {isCancelled ? 'Cancelled reason:' : 'Error:'}
      </p>
      <p className="text-gray-500">{errorText}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main                                                               */
/* ------------------------------------------------------------------ */

const ToolFallbackImpl: ToolCallMessagePartComponent = ({
  toolName,
  argsText,
  result,
  status,
}) => {
  const isCancelled =
    status?.type === 'incomplete' && status.reason === 'cancelled';

  return (
    <ToolFallbackRoot>
      <ToolFallbackTrigger toolName={toolName} status={status} />
      <ToolFallbackContent>
        <ToolFallbackError status={status} />
        <ToolFallbackArgs
          argsText={argsText}
          className={cn(isCancelled && 'opacity-60')}
        />
        {!isCancelled && <ToolFallbackResult result={result} />}
      </ToolFallbackContent>
    </ToolFallbackRoot>
  );
};

const ToolFallback = memo(
  ToolFallbackImpl,
) as unknown as ToolCallMessagePartComponent & {
  Root: typeof ToolFallbackRoot;
  Trigger: typeof ToolFallbackTrigger;
  Content: typeof ToolFallbackContent;
  Args: typeof ToolFallbackArgs;
  Result: typeof ToolFallbackResult;
  Error: typeof ToolFallbackError;
};

ToolFallback.displayName = 'ToolFallback';
ToolFallback.Root = ToolFallbackRoot;
ToolFallback.Trigger = ToolFallbackTrigger;
ToolFallback.Content = ToolFallbackContent;
ToolFallback.Args = ToolFallbackArgs;
ToolFallback.Result = ToolFallbackResult;
ToolFallback.Error = ToolFallbackError;

export {
  ToolFallback,
  ToolFallbackRoot,
  ToolFallbackTrigger,
  ToolFallbackContent,
  ToolFallbackArgs,
  ToolFallbackResult,
  ToolFallbackError,
};
